// client/src/features/equipment/dates.ts — DateOnly arithmetic for the revision timeline ·
// MAINT-006.
//
// A `DateOnly` on the wire is `YYYY-MM-DD` with no time and no zone (D8), and `lib/format.ts`
// formats one by SLICING THE STRING rather than by building a `Date`, because a Date round-trip
// can shift a business date across midnight in either direction. The timeline needs one thing
// that formatting does not — the date a ladder rung falls on, which is the scadență minus a
// number of days — so the arithmetic is done here, once, in UTC.
//
// UTC and not the browser's zone: `Date.UTC` gives a fixed-length day, so „minus 60 days" is
// exactly 60×86 400 000 ms and never 59 or 61 because a DST boundary fell inside the span. The
// value goes in as three integers and comes out as three integers; no local calendar is
// consulted at any point.

/** `"2026-08-31"` → `{ year: 2026, month: 8, day: 31 }`, or null when it is not a DateOnly. */
function parts(dateOnly: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (match === null) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

/** `addDays("2026-08-31", -60)` → `"2026-07-02"`. Returns null on anything but a DateOnly. */
export function addDays(dateOnly: string, days: number): string | null {
  const parsed = parts(dateOnly);
  if (parsed === null) return null;

  const [year, month, day] = parsed;
  const shifted = new Date(Date.UTC(year, month - 1, day) + days * 86_400_000);

  return shifted.toISOString().slice(0, 10);
}

/**
 * Whole days from `from` to `to`, both DateOnly. Null when either is not one.
 *
 * Used ONLY for dates the client itself derived — the rung dates above — never to recompute a
 * figure the server sent: `EquipmentListItem.daysUntilRevision` is the server's own count and
 * every screen renders that one (D-705).
 */
export function daysBetween(from: string, to: string): number | null {
  const start = parts(from);
  const end = parts(to);
  if (start === null || end === null) return null;

  const startMs = Date.UTC(start[0], start[1] - 1, start[2]);
  const endMs = Date.UTC(end[0], end[1] - 1, end[2]);

  return Math.round((endMs - startMs) / 86_400_000);
}

/** `"2026-08-31"` → `"2026-08"`; the calendar groups on this. Null on anything else. */
export function monthOf(dateOnly: string): string | null {
  return parts(dateOnly) === null ? null : dateOnly.slice(0, 7);
}
